import {QueryFunction, QueryKey, UseQueryOptions, useQuery} from '@tanstack/react-query';
import {Database} from '@tnc/shared';

import {ColumnSelectionT, CombinedColumnsT} from '@/lib/types/query.types';
import {AllTablesT, FlattenT} from '@/lib/types/utility.types';

import {convertColumnsToString, getQueryKey} from '@/lib/helpers';
import {SupabaseInstance} from '@/lib/utils';

import {useAuth} from '@/providers/components/authProvider';

/*******************************************************************
 * Types
 *******************************************************************/
// Type for the response of the useItemQuery hook
type UseItemQueryResponseT<T extends keyof AllTablesT, C extends ColumnSelectionT<T>[]> = CombinedColumnsT<T, C>;

type UseItemQueryPropsT<
  T extends keyof Database['public']['Tables'],
  C extends ColumnSelectionT<T>[] = ['*'],
  TData = CombinedColumnsT<T, C>,
  TError extends Error = Error,
  TQueryFnData = CombinedColumnsT<T, C>,
  TQueryKey extends QueryKey = QueryKey,
> = FlattenT<
  Partial<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>> & {
    table: T;
    id: number;
    columns?: C;
    enabled?: boolean;
    protect?: boolean;
    idColumn?: string;
  }
>;

/**
 * Fetches an item row from the database
 * @param {T} table - The table to fetch from
 * @param {number} id - The id of the item to fetch
 * @param {C} columns - The columns to select
 * @returns {Promise<CombinedColumnsT<T, C> | null>} - The item row
 */
async function fetchItem<T extends keyof Database['public']['Tables'], C extends ColumnSelectionT<T>[]>(
  table: T,
  id: number,
  columns: C,
  idColumn: string = `${table.toLowerCase()}_id`
) {
  return SupabaseInstance.supabase
    .from(table)
    .select<string, CombinedColumnsT<T, C>>(convertColumnsToString(columns), {count: 'exact'})
    .eq(idColumn, id)
    .single<CombinedColumnsT<T, C>>()
    .then(({data, error}) => {
      if (error) throw error;
      if (!data) throw new Error('No data returned');
      return data;
    });
}

/**
 * Fetches a single item from the database
 * @param {T} table - The table to fetch from
 * @param {number} id - The id of the item to fetch
 * @param {boolean} enabled - Whether the query is enabled
 * @param {C} columns - The columns to select
 * @returns {ReturnType<typeof useQuery<CombinedColumnsT<T, C>>>} the query
 */
export const useItemQuery = <T extends keyof Database['public']['Tables'], C extends ColumnSelectionT<T>[] = ['*']>({
  table,
  id,
  columns = ['*'] as C,
  enabled: _enabled = true,
  idColumn,
  protect,
  ...rest
}: UseItemQueryPropsT<T, C>) => {
  const {isAdministrator, isAuthenticated} = useAuth();
  const enabled = (protect ? isAdministrator && _enabled : _enabled) && isAuthenticated;

  const queryFn: QueryFunction<UseItemQueryResponseT<T, C>, QueryKey> = async () => {
    return fetchItem<T, C>(table, id, columns, idColumn);
  };

  // Options for the query
  const queryOptions: UseQueryOptions<UseItemQueryResponseT<T, C>, Error, UseItemQueryResponseT<T, C>, QueryKey> = {
    enabled,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retryOnMount: false,
    ...rest,
    queryKey: getQueryKey.detail(table, id, columns, enabled),
    queryFn,
  };

  return useQuery(queryOptions);
};
